import { Fragment, useEffect, useCallback, useState, useRef, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import TwoColumnDescriptionList from "../TwoColumnDescriptionList";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAppointmentsByClientId,
  fetchAppointmentById,
} from "../../../store/thunks/appointmentsThunk";
import FutureAppointments from "../FutureAppointments";
import StepsVerticalListV2 from "../stepsVerticalListV2";
import { AppointmentClientInfo } from "./AppointmentClientInfo";
import { AppointmentInvoices } from "./AppointmentInvoices";
import {
  clearClientAppointments,
  clearSelectedAppointment,
  clearAppointmentUpdated,
} from "../../../store/slices/appointmentsSlice";
import { AppointmentPayments } from "./AppointmentPayments";
import { fetchInvoicePaymentsByAppointmentId } from "../../../store/thunks/paymentsThunk";
import { fetchInvoicesByAppointmentId } from "../../../store/thunks/invoiceThunk";
import {
  clearAppointmentPayments,
  clearInvoicePayments,
} from "../../../store/slices/paymentsSlice";
import { clearAppointmentInvoices } from "../../../store/slices/invoiceSlice";
import ClientJourneyProgressBar from "../../../components/ui/ClientJourneyProgressBar";
import { fetchClientJourneyById } from "../../../store/thunks/clientJourneyThunk";

export default function AppointmentSummary({
  open,
  onClose,
  onAppointmentClick,
  appointment,
  onReschedule,
  preventClientAppointmentsClear = false, // New prop to control cleanup behavior
}) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const scrollContainerRef = useRef(null);
  const reduxSelectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment
  );
  const appointmentUpdated = useSelector(
    (state) => state.appointments.appointmentUpdated
  );

  // Use passed appointment prop if available, otherwise fall back to Redux state
  const selectedAppointment = appointment || reduxSelectedAppointment;
  const appointmentInvoices = useSelector(
    (state) => state.invoices.appointmentInvoices
  );
  const futureAppointments = useSelector(
    (state) => state.appointments.clientAppointments
  );
  const payments = useSelector((state) => state.payments.appointmentPayments);

  // Memoize expensive computations
  const shouldShowInvoices = useMemo(() => {
    return selectedAppointment?.history?.some(
      (entry) =>
        entry.status === "Treated" ||
        entry.status === "Cancelled - Charge Fee" ||
        entry.status === "No Show - Charge Fee"
    ) || appointmentInvoices?.length > 0;
  }, [selectedAppointment?.history, appointmentInvoices?.length]);

  const shouldShowPayments = useMemo(() => {
    return appointmentInvoices?.length > 0 || payments?.length > 0;
  }, [appointmentInvoices?.length, payments?.length]);

  // Function to refresh all appointment data with parallel execution
  const refreshAppointmentData = useCallback(async () => {
    if (selectedAppointment?._id && !isLoading) {
      setIsLoading(true);
      try {
        console.log(`[AppointmentSummary] Starting parallel data fetch for appointment ${selectedAppointment._id}`);
        
        // Execute all API calls in parallel for better performance
        const promises = [
          dispatch(fetchAppointmentById(selectedAppointment._id)),
          dispatch(fetchInvoicesByAppointmentId(selectedAppointment._id)),
          dispatch(fetchInvoicePaymentsByAppointmentId(selectedAppointment._id))
        ];

        // Add client-related calls if client exists
        if (selectedAppointment.client?._id) {
          promises.push(
            dispatch(fetchAppointmentsByClientId(selectedAppointment.client._id)),
            dispatch(fetchClientJourneyById(selectedAppointment.client._id))
          );
        }

        await Promise.all(promises);
        console.log(`[AppointmentSummary] Completed parallel data fetch for appointment ${selectedAppointment._id}`);
      } catch (error) {
        console.error(`[AppointmentSummary] Error fetching appointment data:`, error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [selectedAppointment?._id, selectedAppointment?.client?._id, dispatch, isLoading]);


  // useEffect to fetch data when component mounts or when selectedAppointment changes
  useEffect(() => {
    if (selectedAppointment?._id && open && !hasLoaded) {
      refreshAppointmentData();
      setHasLoaded(true);
    }
  }, [selectedAppointment?._id, open, hasLoaded, refreshAppointmentData]);

  // Reset hasLoaded when modal closes
  useEffect(() => {
    if (!open) {
      setHasLoaded(false);
    }
  }, [open]);

  // Listen for appointment updates - Redux already updated the state, just clear the flag
  useEffect(() => {
    if (appointmentUpdated && selectedAppointment?._id && open) {
      console.log('[AppointmentSummary] Appointment updated via Redux state, clearing flag...');
      // Clear the appointmentUpdated flag - no refresh needed since Redux state is already updated
      dispatch(clearAppointmentUpdated());
    }
  }, [appointmentUpdated, selectedAppointment?._id, open, dispatch]);

  // useEffect to reload data when selectedAppointment ID changes (e.g., clicking future appointments)
  useEffect(() => {
    if (selectedAppointment?._id && open && hasLoaded) {
      console.log('[AppointmentSummary] Selected appointment changed, reloading all data...');
      // Clear existing data first to show fresh data for the new appointment
      dispatch(clearAppointmentPayments());
      dispatch(clearAppointmentInvoices());
      dispatch(clearInvoicePayments());
      
      // Reload all data for the new appointment
      refreshAppointmentData();
    }
  }, [selectedAppointment?._id]);

  const handleRescheduleClick = () => {
    onClose();
    onReschedule(selectedAppointment);
  };

  // Function to handle appointment click from FutureAppointments
  const handleFutureAppointmentClick = (clickedAppointment) => {
    console.log('[AppointmentSummary] Future appointment clicked, scrolling to top...');
    // Scroll to top of the modal
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // Call the parent's onAppointmentClick if provided
    if (onAppointmentClick) {
      onAppointmentClick(clickedAppointment);
    }
  };

  const handleClose = useCallback(() => {
    // Clear all related data to prevent storage issues
    dispatch(clearAppointmentPayments());
    dispatch(clearAppointmentInvoices());
    dispatch(clearInvoicePayments());
    dispatch(clearSelectedAppointment());
    dispatch(clearAppointmentUpdated());
    
    // Clear client appointments to free up memory (unless prevented by parent)
    if (!preventClientAppointmentsClear) {
      dispatch(clearClientAppointments());
    }
    
    // Reset loading state
    setHasLoaded(false);
    setIsLoading(false);
    
    onClose();
  }, [dispatch, preventClientAppointmentsClear, onClose]);

  // Set up global scroll function for appointments clicked from client modal
  useEffect(() => {
    const scrollToTop = () => {
      console.log('[AppointmentSummary] Scrolling to top from client modal appointment click...');
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    window.scrollAppointmentSummaryToTop = scrollToTop;

    return () => {
      // Cleanup
      delete window.scrollAppointmentSummaryToTop;
    };
  }, []);

  console.log("[APPOINTMENT SUMMARY]: This is the appointment", appointment);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-600/75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full">
                <Dialog.Panel className="pointer-events-auto w-[70vw] h-full flex flex-col bg-white shadow-lg">
                  <div ref={scrollContainerRef} className="flex-1 flex flex-col overflow-y-scroll bg-gray-50/30">
                    <div className="px-4 sm:px-6 border-b border-gray-100 bg-white/50 backdrop-blur-sm">
                      <div className="flex items-center justify-between py-3">
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Appointment Summary
                        </Dialog.Title>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            onClick={handleClose}
                            className="rounded-sm text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-950 focus:ring-offset-1 p-1 hover:bg-gray-100 transition-all">
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Client Journey Progress Bar */}
                    {selectedAppointment?.client?._id && (
                      <div className="px-4 sm:px-6 py-4 pb-6">
                        <div className="bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-4 hover:shadow-md transition-all">
                          <ClientJourneyProgressBar
                            clientId={selectedAppointment.client._id}
                          />
                        </div>
                      </div>
                    )}

                    <div className="relative flex-1 px-4 sm:px-6 py-4">
                      {isLoading && (
                        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        <div className="lg:col-span-4 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          <AppointmentClientInfo appointment={selectedAppointment} />
                        </div>
                        {shouldShowInvoices && (
                          <div className="lg:col-span-4 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                            <AppointmentInvoices />
                          </div>
                        )}

                        {shouldShowPayments && (
                          <div className="lg:col-span-4 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                            <AppointmentPayments />
                          </div>
                        )}
                        <div className="lg:col-span-2 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          <TwoColumnDescriptionList
                            type="appointment"
                            appointment={selectedAppointment}
                            onReschedule={handleRescheduleClick}
                          />
                        </div>
                        <div className="lg:col-span-2 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          <StepsVerticalListV2
                            appointment={selectedAppointment}
                          />
                        </div>
                        
                        {/* Future Appointments Table - Full Width at Bottom */}
                        <div className="lg:col-span-4 bg-white rounded-sm shadow-sm border border-gray-100/50 px-4 py-3 hover:shadow-md transition-all">
                          <FutureAppointments
                            appointments={futureAppointments}
                            onAppointmentClick={handleFutureAppointmentClick}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
